import type { CommentableContentObject } from 'models';
import { type RefObject, useCallback, useRef } from 'react';
import { usePermissions } from 'permissions';
import type { InputRef, RichTextEditorBase } from 'ui';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { useCommunityMentions } from 'features/communities/hooks';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { useProgramMentions } from 'features/programs/hooks';
import { type CommentMode, CreateCommentForm } from '../create-comment-form';
import { useContentObjectRepliesQuery } from '../queries';
import { Comments } from './comments';
import { CommentsCard } from './comments-card';
import { CommentsSkeleton } from './comments-skeleton';

type P = {
  card?: boolean;
  commentFormContainerRef?: RefObject<HTMLDivElement | null>;
  contentObject: CommentableContentObject;
  editorRef?: RefObject<null | RichTextEditorBase>;
  initialMode?: CommentMode;
  inputRef?: RefObject<InputRef | null>;
  scrollOnMount?: boolean;
};

const CommentsWrapper = ({
  card,
  commentFormContainerRef: commentFormContainerRefProp,
  contentObject,
  editorRef: editorRefProp,
  initialMode,
  inputRef: inputRefProp,
  scrollOnMount,
}: P) => {
  const editorRef = useRef<RichTextEditorBase>(null);
  const inputRef = useRef<InputRef>(null);
  const commentFormContainerRef = useRef<HTMLDivElement>(null);

  const getCommunityId = useCallback(
    () => (contentObject.contentableType === 'Community' ? contentObject.postInCommunities[0].id : undefined),
    [contentObject],
  );

  const handleOnRequestCommunityMentions = useCommunityMentions(getCommunityId);

  const handleOnRequestProgramMentions = useProgramMentions(
    contentObject.contentableType === 'Program' ? contentObject.program.id : undefined,
  );

  const onRequestMentions = useCallback(
    async (value: string) => {
      switch (contentObject.contentableType) {
        case 'Community':
          return handleOnRequestCommunityMentions(value);
        case 'Program':
          return handleOnRequestProgramMentions(value);
      }
    },
    [contentObject.contentableType, handleOnRequestCommunityMentions, handleOnRequestProgramMentions],
  );

  const { canComment, canDeleteComment } = usePermissions();

  const {
    data: comments,
    isFetching,
    isLoading,
  } = useContentObjectRepliesQuery({
    id: contentObject.id,
    options: {
      gcTime: 0,
    },
  });

  if ((isLoading && isFetching) || !comments) {
    return <CommentsSkeleton card={card} />;
  }

  return card ? (
    <CommentsCard
      canComment={canComment({ contentObject })}
      canDeleteComment={(comment) => canDeleteComment({ comment, contentObject })}
      commentFormContainerRef={commentFormContainerRefProp ?? commentFormContainerRef}
      comments={comments}
      contentObjectId={contentObject.id}
      contentObjectType={contentObject.type}
      editorRef={editorRefProp ?? editorRef}
      inputRef={inputRefProp ?? inputRef}
      onRequestMentions={onRequestMentions}
    />
  ) : (
    <>
      <Comments
        canComment={canComment({ contentObject })}
        canDeleteComment={(comment) => canDeleteComment({ comment, contentObject })}
        comments={comments}
        contentObjectId={contentObject.id}
        contentObjectType={contentObject.type}
        createCommentForm={false}
        onRequestMentions={onRequestMentions}
      />
      {canComment({ contentObject }) ? (
        <CreateCommentForm
          commentFormContainerRef={commentFormContainerRefProp ?? commentFormContainerRef}
          contentObjectId={contentObject.id}
          editorRef={editorRefProp ?? editorRef}
          initialMode={initialMode}
          inputRef={inputRefProp ?? inputRef}
          onRequestMentions={onRequestMentions}
          scrollOnMount={scrollOnMount}
        />
      ) : null}
    </>
  );
};

export default CommentsWrapper;
